import { useCallback, useEffect, useState } from 'react';
import { getUserSubscriptions } from '../services/payments';

export function useSubscriptions(isLoggedIn) {
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(false);

  const refetch = useCallback(async () => {
    if (!isLoggedIn) {
      setSubscriptions([]);
      return;
    }

    setLoading(true);
    try {
      const res = await getUserSubscriptions();
      setSubscriptions(res?.data?.subscriptions || []);
    } catch {
      setSubscriptions([]);
    } finally {
      setLoading(false);
    }
  }, [isLoggedIn]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  const isActiveSubscription = useCallback((sub) => {
    return Boolean(sub.active && (sub.status === 'completed' || sub.is_subscription));
  }, []);

  const activeSubscription = subscriptions.find(isActiveSubscription) || null;

  const hasActiveSubscription = Boolean(activeSubscription);

  const activeCategoryId = activeSubscription?.class_id ?? null;

  const isPlanSubscribed = useCallback(
    (plan) => {
      const planId = Number(plan?.id);
      if (!Number.isFinite(planId)) return false;

      return subscriptions.some(
        (sub) => isActiveSubscription(sub) && sub.plan?.id === planId
      );
    },
    [subscriptions, isActiveSubscription]
  );

  const isCategorySubscribed = useCallback(
    (categoryId) => {
      if (categoryId == null) return false;

      return subscriptions.some((sub) => {
        if (!isActiveSubscription(sub)) return false;

        const subCategoryId = sub.class_id ?? sub.plan?.class_id ?? sub.category_id;
        return String(subCategoryId) === String(categoryId);
      });
    },
    [subscriptions, isActiveSubscription]
  );

  return {
    subscriptions,
    loading,
    refetch,
    isPlanSubscribed,
    isCategorySubscribed,
    hasActiveSubscription,
    activeCategoryId,
  };
}
